import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { VoucherSurface } from '@aurumrun/analytics'
import { useBalanceStore } from '~/stores/balance'
import { useBonusStore } from '~/stores/bonus'
import { validateCoupon, redeemCoupon, cancelPendingCoupon, type CouponPreview, type PendingDepositMatchVoucher } from '~/composables/api/useBonusApi'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'

/**
 * The voucher validate → terms → redeem flow, shared by the wallet page
 * (`components/bonus/VoucherActivation.vue`) and the deposit modal's bonus-code
 * field (`components/deposit/DepositBonusCode.vue`).
 *
 * It lives here rather than in either component so the two surfaces cannot
 * drift: they hit the same endpoints, surface the same toasts, and refresh the
 * same stores. Redemption is deliberately not cached in the bonus store — it is
 * a one-shot request/response; what IS shared app-wide is the wagering status,
 * which this refreshes on success.
 */

/**
 * Module-scoped on purpose. A redeemed deposit_match grant stays `pending`, so
 * `hasActiveBonus` cannot describe it, and the deposit modal unmounts its whole
 * picker subtree the moment a method is chosen — a component-local flag would be
 * gone by the time the player comes back, re-showing the entry form for a code
 * that now answers ALREADY_REDEEMED. Lives for the app session; a reload drops it,
 * which is the backend gap (pending grants are absent from the wagering status).
 */
const redeemedThisSession = ref(false)

/**
 * The coupon that was redeemed this session, module-scoped for the same reason
 * as the latch above: `preview` is created per-composable-instance, so when the
 * deposit modal unmounts its picker subtree (or the modal is closed and
 * reopened) the per-instance preview is gone and the activated strip loses the
 * coupon's own numbers — match %, up-to, wagering, min deposit. Holding the
 * redeemed coupon here keeps those terms across remounts. Session-lived like the
 * latch; a reload drops it (the backend gap: pending grants aren't in the
 * wagering status).
 */
const redeemedCoupon = ref<CouponPreview | null>(null)

/**
 * A page reload drops the two module refs above, so a still-pending deposit-match
 * voucher would fall back to the entry form after F5 — its grant lives on the
 * backend but isn't in the wagering status, so nothing else restores it. Persist
 * exactly that case to sessionStorage (tab-scoped, short-lived like the voucher)
 * and rehydrate on load, so the deposit field keeps its activated / deactivate
 * state across a reload. Fixed & free bonuses are NOT persisted: once active they
 * come back via the wagering status, and once converted the entry form is right.
 */
const REDEEMED_STORAGE_KEY = 'voucher:redeemed-deposit-match'

function persistRedeemed(coupon: CouponPreview | null): void {
  if (!import.meta.client) return
  try {
    if (coupon && coupon.type === 'deposit_match_percent') {
      sessionStorage.setItem(REDEEMED_STORAGE_KEY, JSON.stringify(coupon))
    } else {
      sessionStorage.removeItem(REDEEMED_STORAGE_KEY)
    }
  } catch { /* storage unavailable — degrade to session-only (pre-reload) */ }
}

// Rehydrate the latch on module load (client only) so a reload keeps a pending
// deposit-match voucher's activated state.
if (import.meta.client) {
  try {
    const raw = sessionStorage.getItem(REDEEMED_STORAGE_KEY)
    if (raw) {
      redeemedCoupon.value = JSON.parse(raw) as CouponPreview
      redeemedThisSession.value = true
    }
  } catch { /* ignore corrupt/absent */ }
}

/**
 * Mirrors the backend exactly: `coupons.code` carries a
 * `CHECK (code ~ '^[A-Z0-9]{4,20}$')`, and both coupon-validate and
 * coupon-redeem re-check the same pattern via `isValidCodeFormat`. A code
 * outside it cannot exist in the table, so sending one is a guaranteed error —
 * gate it here instead and keep the user out of a pointless round trip.
 */
export const CODE_PATTERN = /^[A-Z0-9]{4,20}$/

/**
 * Read-only accessor for the session-scoped redemption state, without spinning
 * up the whole validate→redeem flow. Both refs are module-scoped, so any surface
 * (e.g. the deposit modal's min-deposit notice) sees the same values the
 * activation component set.
 */
export function usePendingVoucher(): {
  redeemedCoupon: Ref<CouponPreview | null>
  justRedeemed: Ref<boolean>
} {
  return { redeemedCoupon, justRedeemed: redeemedThisSession }
}

/**
 * Adopt a backend-reported pending deposit-match (from the wagering status) as
 * the redeemed voucher, so its activated / deactivate state is restored after a
 * reload or in a brand-new session even when the sessionStorage latch is empty.
 * No-op if a coupon is already latched (a locally-redeemed one is fresher) or if
 * the backend reports none.
 */
export function adoptPendingDepositMatch(pending: PendingDepositMatchVoucher | null): void {
  if (!pending || redeemedCoupon.value) return
  const coupon: CouponPreview = {
    code: pending.code,
    name: pending.name,
    description: null,
    type: 'deposit_match_percent',
    wageringMultiplier: pending.wageringMultiplier,
    expiresAt: null,
    matchPercent: pending.matchPercent,
    minDeposit: pending.minDeposit,
    maxBonus: pending.maxBonus,
  }
  redeemedCoupon.value = coupon
  redeemedThisSession.value = true
  persistRedeemed(coupon)
}

/**
 * Wipe the session-scoped redemption latch (module refs + sessionStorage). The
 * refs live at module scope for the whole app session, so on logout the next user
 * on this tab would otherwise inherit the previous user's voucher state — call
 * this from the logout flow.
 */
export function resetVoucherActivationState(): void {
  redeemedThisSession.value = false
  redeemedCoupon.value = null
  persistRedeemed(null)
}

/**
 * Reconcile the session latch against the backend wagering status, called on every
 * status refresh from the deposit field:
 *  • a pending deposit-match is reported → adopt it (restores the activated /
 *    deactivate state after a reload or in a fresh session).
 *  • no pending AND no active bonus → any latched deposit-match voucher is gone
 *    (funded then converted+ended, lost, expired, or cancelled in another tab) →
 *    drop the stale latch so the field returns to the entry form instead of
 *    showing "Activated / Deactivate" for a voucher that no longer exists.
 * A funded, still-active bonus (pending null, hasActiveBonus true) keeps the latch
 * — the field shows the active bonus, not a re-offer.
 */
export function reconcilePendingDepositMatch(
  pending: PendingDepositMatchVoucher | null,
  hasActiveBonus: boolean,
): void {
  if (pending) {
    adoptPendingDepositMatch(pending)
    return
  }
  if (!hasActiveBonus && redeemedCoupon.value?.type === 'deposit_match_percent') {
    resetVoucherActivationState()
  }
}

/** The backend's `normalizeCode`: what actually gets matched, case-insensitively. */
function normalizeCode(code: string): string {
  return code.trim().toUpperCase()
}

/** Longest code the backend will accept — bind it to the input's `maxlength`. */
export const CODE_MAX_LENGTH = 20

export interface UseVoucherActivation {
  code: Ref<string>
  /** The validated coupon; kept after redeem so callers can render its terms. */
  preview: Ref<CouponPreview | null>
  showTerms: Ref<boolean>
  isValidating: Ref<boolean>
  isRedeeming: Ref<boolean>
  /** Whether the typed code can exist at all — drives the Activate button. */
  isCodeValid: ComputedRef<boolean>
  /** True once a code was redeemed in this app session. Survives remounts. */
  justRedeemed: Ref<boolean>
  /** The coupon redeemed this session — survives remounts, unlike `preview`. */
  redeemedCoupon: Ref<CouponPreview | null>
  /** True while a pending-voucher deactivation is in flight. */
  isCancelling: Ref<boolean>
  validate: () => Promise<void>
  confirm: () => Promise<boolean>
  /**
   * Detach a pending (not-yet-funded) deposit-match voucher so the code is
   * reusable. Resolves true once the backend confirms it was detached.
   */
  cancelPending: () => Promise<boolean>
  /** Forget the redemption — for when the grant it described is gone. */
  clearRedeemed: () => void
}

export function useVoucherActivation(surface: VoucherSurface): UseVoucherActivation {
  const { t } = useI18n()
  const toast = useToast()
  const { getAccessToken } = useSupabaseAuth()
  const balanceStore = useBalanceStore()
  const bonusStore = useBonusStore()

  const code = ref('')
  const isValidating = ref(false)
  const isRedeeming = ref(false)
  const isCancelling = ref(false)
  const showTerms = ref(false)
  /** The validated coupon, kept after redeem so callers can render its terms. */
  const preview = ref<CouponPreview | null>(null)

  const isCodeValid = computed(() => CODE_PATTERN.test(normalizeCode(code.value)))

  /** Validate the entered code and, if it is good, open the terms step. */
  async function validate(): Promise<void> {
    const trimmed = code.value.trim()
    // Guarded rather than assumed: callers disable their button on
    // `isCodeValid`, but Enter reaches a form even while the button is disabled.
    if (!isCodeValid.value || isValidating.value) return

    isValidating.value = true
    preview.value = null
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        toast.error(t('wallet.bonus.errorActivateFailed'))
        return
      }
      const res = await validateCoupon(accessToken, trimmed)
      if (!res.valid || !res.coupon) {
        toast.error(t('wallet.bonus.errorActivateFailed'))
        return
      }
      preview.value = res.coupon
      showTerms.value = true
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('wallet.bonus.errorActivateFailed')))
    } finally {
      isValidating.value = false
    }
  }

  /** Redeem the validated code. Resolves true once the grant exists. */
  async function confirm(): Promise<boolean> {
    const trimmed = code.value.trim()
    // Same rule as validate(), stated the same way — confirm is only reachable
    // behind a successful validate, but two spellings of one rule invite drift.
    if (!isCodeValid.value || isRedeeming.value) return false

    isRedeeming.value = true
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        toast.error(t('wallet.bonus.errorActivateFailed'))
        return false
      }
      const res = await redeemCoupon(accessToken, trimmed)
      if (!res.success) {
        toast.error(t('wallet.bonus.errorActivateFailed'))
        return false
      }

      // The grant exists from here on. Everything below is local bookkeeping,
      // so nothing below may downgrade this into a failure.
      redeemedThisSession.value = true
      // Stash the full coupon at module scope so the activated strip keeps its
      // terms after a remount (the per-instance `preview` won't survive one), and
      // persist a pending deposit-match so it also survives a page reload.
      redeemedCoupon.value = preview.value
      persistRedeemed(preview.value)
      showTerms.value = false
      code.value = ''
      useAnalytics().track('voucher_action', { action: 'applied', surface })

      // Deposit-match vouchers create a pending grant that only funds on the
      // qualifying deposit; fixed-bonus vouchers credit immediately.
      const isDepositMatch = res.coupon.type === 'deposit_match_percent'
      toast.success(
        t('wallet.bonus.activatedToast'),
        isDepositMatch ? t('wallet.bonus.activatedDepositMatch') : t('wallet.bonus.activatedFixed'),
      )

      // Refresh balances + wagering status so a fixed-bonus grant surfaces at
      // once — but deliberately outside the failure path. A transient status
      // outage must not chase the success toast with an error one, nor drop the
      // latch and re-offer the entry form for a code that now answers
      // ALREADY_REDEEMED. The stores refresh again on the next open either way.
      await Promise.all([
        balanceStore.fetchBalance(accessToken),
        bonusStore.fetchStatus(accessToken),
      ]).catch(() => {})

      return true
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('wallet.bonus.errorActivateFailed')))
      return false
    } finally {
      isRedeeming.value = false
    }
  }

  /**
   * Detach the pending deposit-match voucher redeemed this session so the code
   * can be used again later. Reads the code from the module-scoped redeemed
   * coupon (the input has long since been cleared). On success, drops the
   * redemption latch and refreshes the stores.
   */
  async function cancelPending(): Promise<boolean> {
    const c = redeemedCoupon.value
    if (!c || isCancelling.value) return false

    isCancelling.value = true
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        toast.error(t('wallet.bonus.errorActivateFailed'))
        return false
      }
      const res = await cancelPendingCoupon(accessToken, c.code)
      if (!res.success) {
        // Nothing pending to detach (already funded, or another tab beat us).
        // Re-sync so the UI reflects reality instead of leaving a dead button.
        await bonusStore.fetchStatus(accessToken).catch(() => {})
        toast.error(t('wallet.bonus.errorActivateFailed'))
        return false
      }

      clearRedeemed()
      useAnalytics().track('voucher_action', { action: 'removed', surface })
      toast.success(t('wallet.deposit.bonus.deactivatedTitle'), t('wallet.deposit.bonus.deactivatedToast'))

      await Promise.all([
        balanceStore.fetchBalance(accessToken),
        bonusStore.fetchStatus(accessToken),
      ]).catch(() => {})

      return true
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('wallet.bonus.errorActivateFailed')))
      return false
    } finally {
      isCancelling.value = false
    }
  }

  function clearRedeemed(): void {
    redeemedThisSession.value = false
    redeemedCoupon.value = null
    preview.value = null
    persistRedeemed(null)
  }

  return {
    code, preview, showTerms, isValidating, isRedeeming, isCodeValid,
    justRedeemed: redeemedThisSession,
    redeemedCoupon,
    isCancelling,
    validate, confirm, cancelPending, clearRedeemed,
  }
}
